import React from 'react';
import Modal from '../ui/Modal';
import Stepper from '../ui/Stepper';
import Toggle from '../ui/Toggle';
import Icon from '../ui/Icon';
import Select from '../ui/Select';
import Input from '../ui/Input';
import SegmentBuilder from '../audience/SegmentBuilder';
import PushFields from '../messaging/PushFields';
import WhatsappFields from '../messaging/WhatsappFields';
import SmsFields from '../messaging/SmsFields';
import EmailFields from '../messaging/EmailFields';
import RcsFields from '../messaging/RcsFields';
import { withPrefix } from '../messaging/fieldPrefix';
import TestSendModal from './TestSendModal';
import EventBus from '../../utils/EventBus';
import { campaignService } from '../../services/campaignService';
import { segmentService } from '../../services/segmentService';
import { filtersToConditions, toMatchType } from '../../utils/segmentConditions';
import styles from './CampaignWizard.module.css';

const STEPS = ['Audience', 'Channel', 'Message', 'Schedule', 'Review'];

// Per-channel "stat" chip is real data now — see channelStats state, fetched
// from /campaigns/channel-stats (historical rates aggregated across every
// campaign actually sent on that channel). Nothing fabricated here anymore.
const CHANNELS = [
  { key: 'push',     ico: 'notifications', name: 'Push + In-app',     desc: 'FCM push notification, plus an in-app inbox entry — sent together, always.', sc: 'rgba(79,155,111,.09)', tc: '#63B887', bg: 'rgba(184,66,66,.1)' },
  { key: 'whatsapp', ico: 'forum',         name: 'WhatsApp',          desc: 'WhatsApp Business templates via Exotel · high open rates.',             sc: 'rgba(160,124,48,.09)', tc: '#C09848', bg: 'rgba(79,155,111,.1)' },
  { key: 'sms',      ico: 'sms',           name: 'SMS',               desc: 'AWS SNS text messages · DLT-compliant templates for India.',            sc: 'rgba(160,124,48,.09)', tc: '#C09848', bg: 'rgba(160,124,48,.1)' },
  { key: 'email',    ico: 'mail',          name: 'Email',             desc: 'Rich HTML emails for newsletters, digests, and re-engagement.',         sc: 'rgba(110,98,168,.09)', tc: '#8878C0', bg: 'rgba(110,98,168,.1)' },
  { key: 'rcs',      ico: 'chat',          name: 'RCS',               desc: 'Rich messaging via MSG91 · pre-approved templates like WhatsApp.',      sc: 'rgba(72,120,176,.09)', tc: '#6090C8', bg: 'rgba(72,120,176,.1)' },
];

// channelStats[channel].delivery_rate is the most meaningful single number
// for this compact chip — click/conversion rate live in the Step5 estimate instead.
function channelStatLabel(stats) {
  if (!stats || !stats.has_data) return 'No data yet';
  return (stats.delivery_rate * 100).toFixed(1) + '% delivered';
}

const SCHEDULES = [
  { key: 'now',       ico: 'bolt',          name: 'Send Now',     desc: 'Deliver immediately after you launch' },
  { key: 'scheduled', ico: 'calendar_month',name: 'Schedule',     desc: 'Pick a specific date and time' },
  { key: 'optimal',   ico: 'auto_awesome',  name: 'Optimal Time', desc: 'AI picks the best send time per user based on their past activity', disabled: true },
];

const DEFAULT_STATE = {
  step: 0,
  editingId: null,
  editingSegmentId: null,
  editingLaunchedAt: null,
  initialSegment: null,
  audience: null,
  channels: ['push'],
  msgTitle: '',
  msgBody: '',
  msgRedirectionType: '',
  msgRedirectionValue: '',
  msgImageUrl: '',
  waTemplateName: '',
  waTemplateParams: '',
  waImageUrl: '',
  waAttributionLinkId: '',
  smsText: '',
  smsTemplateId: '',
  smsAttributionLinkId: '',
  emailSubject: '',
  emailHtml: '',
  rcsTemplateId: '',
  rcsTemplateParams: '',
  rcsAttributionLinkId: '',
  schedType: 'now',
  schedDate: '',
  schedTime: '',
  schedTZ: 'IST +5:30',
  quietHours: true,
  stopOnConvert: true,
  freqCap: true,
  controlGroup: false,
  campName: '',
  saving: false,
  testSendChannel: null,
};

class Step1Audience extends React.Component {
  render() {
    const { onChange, initialSegment } = this.props;
    return (
      <div>
        <p className={styles.stepIntro}>Who should receive this campaign?</p>
        <SegmentBuilder
          initialSegment={initialSegment}
          onChange={onChange}
          onSaved={(segment) => onChange({
            mode: 'saved',
            selectedSegId: segment.id,
            filters: [],
            op: 'AND',
            reach: segment.user_count,
          })}
        />
      </div>
    );
  }
}

class Step2Channel extends React.Component {
  render() {
    // `value` is now an array — a campaign can target several channels at once,
    // and every matched user is sent on all of them. Clicking toggles a channel
    // in/out (min 1 enforced by the Continue gate in the wizard footer).
    const { value, onToggle, channelStats } = this.props;
    return (
      <div>
        <p className={styles.stepIntro}>How should this message be delivered? Pick one or more — each recipient gets every channel you select.</p>
        <div className={styles.chList}>
          {CHANNELS.map(ch => {
            const selected = value.includes(ch.key);
            return (
              <div
                key={ch.key}
                className={[styles.chRow, selected ? styles.chOn : ''].join(' ')}
                onClick={() => onToggle(ch.key)}
              >
                <div className={styles.chIco} style={{ background: ch.bg }}>
                  <Icon name={ch.ico} size={20} />
                </div>
                <div className={styles.chInfo}>
                  <div className={styles.chName}>{ch.name}</div>
                  <div className={styles.chDesc}>{ch.desc}</div>
                </div>
                <div className={styles.chStat} style={{ background: ch.sc, color: ch.tc }}>{channelStatLabel(channelStats?.[ch.key])}</div>
                <div className={[styles.chRadio, selected ? styles.chRadioOn : ''].join(' ')}>
                  {selected && <Icon name="check" size={12} />}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

// One channel's compose fields — the per-channel state is prefixed
// (msg*/wa*/sms*/email*/rcs*) so several render side by side without collision.
function ChannelFields({ channel, onField, ...rest }) {
  if (channel === 'whatsapp') {
    return <WhatsappFields templateName={rest.waTemplateName} templateParams={rest.waTemplateParams} imageUrl={rest.waImageUrl} attributionLinkId={rest.waAttributionLinkId} onChange={withPrefix('wa', onField)} />;
  }
  if (channel === 'sms') {
    return <SmsFields text={rest.smsText} templateId={rest.smsTemplateId} attributionLinkId={rest.smsAttributionLinkId} onChange={withPrefix('sms', onField)} />;
  }
  if (channel === 'email') {
    return <EmailFields subject={rest.emailSubject} html={rest.emailHtml} onChange={withPrefix('email', onField)} />;
  }
  if (channel === 'rcs') {
    return <RcsFields templateId={rest.rcsTemplateId} templateParams={rest.rcsTemplateParams} attributionLinkId={rest.rcsAttributionLinkId} onChange={withPrefix('rcs', onField)} />;
  }
  return <PushFields title={rest.msgTitle} body={rest.msgBody} redirectionType={rest.msgRedirectionType} redirectionValue={rest.msgRedirectionValue} imageUrl={rest.msgImageUrl} onChange={withPrefix('msg', onField)} />;
}

class Step3Message extends React.Component {
  render() {
    const { channels, onField, onTestSend, ...rest } = this.props;
    // A composer section per selected channel — stacked, each labeled, so a
    // push+email campaign is authored in one step.
    return (
      <div>
        {channels.map((channel) => {
          const chInfo = CHANNELS.find((c) => c.key === channel) || CHANNELS[0];
          return (
            <div key={channel} className={styles.msgChannelBlock}>
              {/* Always shown (not just for multi-channel campaigns) — it's the
                  only place a per-channel "Send test" action can live. */}
              <div className={styles.msgChannelHead}>
                <Icon name={chInfo.ico} size={16} />
                <span className={styles.msgChannelName}>{chInfo.name}</span>
                <button type="button" className={styles.testSendBtn} onClick={() => onTestSend(channel)}>
                  <Icon name="send" size={13} /> Send test
                </button>
              </div>
              <div className={styles.msgChannelBody}>
                <ChannelFields channel={channel} onField={onField} {...rest} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }
}

class Step4Schedule extends React.Component {
  render() {
    const { schedType, schedDate, schedTime, schedTZ, quietHours, stopOnConvert, freqCap, controlGroup, onField } = this.props;

    return (
      <div>
        <p className={styles.stepIntro}>When should this campaign go out?</p>
        <div className={styles.schedList}>
          {SCHEDULES.map(sc => (
            <div
              key={sc.key}
              className={[styles.schedRow, schedType === sc.key ? styles.schedOn : '', sc.disabled ? styles.schedDisabled : ''].join(' ')}
              onClick={() => !sc.disabled && onField('schedType', sc.key)}
              title={sc.disabled ? 'Coming soon' : undefined}
            >
              <div className={styles.schedIco}><Icon name={sc.ico} size={20} /></div>
              <div>
                <div className={styles.schedName}>
                  {sc.name}
                  {sc.disabled && <span className={styles.soonBadge}>Coming soon</span>}
                </div>
                <div className={styles.schedDesc}>{sc.desc}</div>
              </div>
              {!sc.disabled && (
                <div className={[styles.schedRadio, schedType === sc.key ? styles.schedRadioOn : ''].join(' ')} />
              )}
            </div>
          ))}
        </div>

        {schedType === 'scheduled' && (
          <div className={styles.schedExtra}>
            <div className={styles.fi}>
              <label className={styles.fiLabel}>Date</label>
              <Input type="date" value={schedDate} onChange={e => onField('schedDate', e.target.value)} />
            </div>
            <div className={styles.fi}>
              <label className={styles.fiLabel}>Time</label>
              <Input type="time" value={schedTime} onChange={e => onField('schedTime', e.target.value)} />
            </div>
            <div className={styles.fi}>
              <label className={styles.fiLabel}>Timezone</label>
              <Select
                value={schedTZ}
                options={['IST +5:30', 'UTC', 'EST −5', 'PST −8'].map((tz) => ({ value: tz, label: tz }))}
                onChange={(val) => onField('schedTZ', val)}
              />
            </div>
          </div>
        )}

        <div className={styles.msLabel}>Delivery settings</div>
        <div className={styles.tglBlock}>
          {[
            { key: 'quietHours',    val: quietHours,    title: 'Quiet hours',          sub: 'Skip delivery between 11 PM – 8 AM IST' },
            { key: 'stopOnConvert', val: stopOnConvert, title: 'Stop on conversion',   sub: 'Don\'t send if user already completed the goal event' },
            { key: 'freqCap',       val: freqCap,       title: 'Frequency cap',        sub: 'Skip users who received a message in the last 24 hours' },
            { key: 'controlGroup',  val: controlGroup,  title: 'Control group (10%)',  sub: 'Hold out a group to measure campaign lift', disabled: true },
          ].map(t => (
            <div key={t.key} className={[styles.tglRow, t.disabled ? styles.tglDisabled : ''].join(' ')} title={t.disabled ? 'Coming soon' : undefined}>
              <div className={styles.tglInfo}>
                <div className={styles.tglTitle}>
                  {t.title}
                  {t.disabled && <span className={styles.soonBadge}>Coming soon</span>}
                </div>
                <div className={styles.tglSub}>{t.sub}</div>
              </div>
              <Toggle checked={t.disabled ? false : t.val} onChange={v => !t.disabled && onField(t.key, v)} disabled={t.disabled} />
            </div>
          ))}
        </div>
      </div>
    );
  }
}

class Step5Review extends React.Component {
  render() {
    const { campName, audience, channels, channelStats, msgTitle, waTemplateName, smsText, emailSubject, rcsTemplateId, schedType, onField } = this.props;

    const scInfo  = SCHEDULES.find(s => s.key === schedType) || SCHEDULES[0];
    // Estimate is keyed on the first selected channel (a single blended
    // estimate across mixed channels would be misleading — each has its own
    // delivery/click profile).
    const primaryChannel = channels[0] || 'push';
    const chInfo  = CHANNELS.find(c => c.key === primaryChannel) || CHANNELS[0];
    const stats   = channelStats?.[primaryChannel];
    const reach   = audience?.reach || 0;
    const fmt     = (rate) => (reach * rate / 1000).toFixed(1) + 'K';
    const channelNames = channels.map((c) => (CHANNELS.find((x) => x.key === c) || {}).name || c).join(', ');
    const summaryByChannel = { push: msgTitle, whatsapp: waTemplateName, sms: smsText, email: emailSubject, rcs: rcsTemplateId };
    const messageSummary = channels.map((c) => summaryByChannel[c]).filter(Boolean).join(' · ') || '—';
    const summary = [
      { k: 'Audience', v: audience?.selectedSegId ? 'Saved Segment' : (audience?.filters?.length ? 'Custom filter (' + (audience?.reach || 0).toLocaleString() + ' users)' : 'Unchanged') },
      { k: channels.length > 1 ? 'Channels' : 'Channel',  v: channelNames },
      { k: 'Message',  v: messageSummary },
      { k: 'Schedule', v: scInfo.name },
    ];

    return (
      <div>
        <p className={styles.stepIntro}>Almost done — give this campaign a name your team will recognise, then review and launch.</p>
        <div className={styles.fi}>
          <label className={styles.fiLabel} style={{ textTransform: 'uppercase', letterSpacing: '.6px' }}>
            Campaign Name <span className={styles.req}>*</span>
          </label>
          <input
            className={[styles.fiInp, styles.campNameInp].join(' ')}
            placeholder="e.g. Cart Recovery — Power Users — July 2026"
            value={campName}
            onChange={e => onField('campName', e.target.value)}
          />
          <div className={styles.fiHint} style={{ marginTop: 6 }}>Visible only to your team, not to users.</div>
        </div>

        <div className={styles.rvLabel}>Review Summary</div>
        <div className={styles.rvWrap}>
          {summary.map(r => (
            <div key={r.k} className={styles.rvRow}>
              <span className={styles.rvKey}>{r.k}</span>
              <span className={styles.rvVal}>{r.v}</span>
            </div>
          ))}
        </div>

        {stats?.has_data ? (
          <div className={styles.rvEsts}>
            <div className={styles.rvEstItem}>
              <div className={styles.rvEstVal}>{fmt(stats.delivery_rate)}</div>
              <div className={styles.rvEstLbl}>Est. Delivered</div>
            </div>
            <div className={styles.rvEstItem}>
              <div className={styles.rvEstVal}>{fmt(stats.click_rate)}</div>
              <div className={styles.rvEstLbl}>Est. Clicks</div>
            </div>
            <div className={styles.rvEstItem}>
              <div className={styles.rvEstVal}>{fmt(stats.conversion_rate)}</div>
              <div className={styles.rvEstLbl}>Est. Conversions</div>
            </div>
          </div>
        ) : (
          <div className={styles.fiHint} style={{ marginTop: 12 }}>
            No historical sends on this channel yet — delivery/click/conversion estimates will show up here once you've launched at least one {chInfo.name.toLowerCase()} campaign.
          </div>
        )}
      </div>
    );
  }
}

class CampaignWizard extends React.Component {
  constructor(props) {
    super(props);
    // channelStats lives outside DEFAULT_STATE on purpose — _openHandler
    // resets to {...DEFAULT_STATE, open: true} every time the wizard opens,
    // and this is fetched once and doesn't need to be re-fetched per open.
    this.state = { ...DEFAULT_STATE, open: false, channelStats: {} };
    this._openHandler = this._openHandler.bind(this);
    this._closeHandler = this._closeHandler.bind(this);
    this.next = this.next.bind(this);
    this.back = this.back.bind(this);
    this.draft = this.draft.bind(this);
    this.launch = this.launch.bind(this);
    this.setField = this.setField.bind(this);
    this.toggleChannel = this.toggleChannel.bind(this);
    this.openTestSend = this.openTestSend.bind(this);
    this.closeTestSend = this.closeTestSend.bind(this);
  }

  componentDidMount() {
    EventBus.on('openWizard', this._openHandler);
    EventBus.on('closeWizard', this._closeHandler);
  }

  componentWillUnmount() {
    EventBus.off('openWizard', this._openHandler);
    EventBus.off('closeWizard', this._closeHandler);
  }

  // Fetched lazily on first open (not on mount, which happens as soon as the
  // Campaigns page loads) and only once — per-channel .catch keeps one failed
  // channel from blanking out the rest, and never leaves an unhandled
  // rejection lying around for a stray page-load-before-login timing.
  _loadChannelStatsOnce() {
    if (this._channelStatsRequested) return;
    this._channelStatsRequested = true;
    Promise.all(CHANNELS.map((ch) =>
      campaignService.channelStats(ch.key)
        .then((stats) => [ch.key, stats])
        .catch(() => [ch.key, null])
    )).then((pairs) => this.setState({ channelStats: Object.fromEntries(pairs) }));
  }

  // Editing an existing campaign: prefill every field from it. The modal only
  // opens once the segment fetch resolves — SegmentBuilder reads
  // `initialSegment` in its constructor only, so it must already be in props
  // by the time it first mounts, or the prefill silently wouldn't show.
  _openHandler(payload) {
    this._loadChannelStatsOnce();
    const campaign = payload?.campaign;
    if (!campaign) {
      this.setState({ ...DEFAULT_STATE, open: true });
      return;
    }

    this.setState({ ...DEFAULT_STATE, open: false });

    // Content is per-channel now (campaign.contents = [{channel, template}]).
    // Pull each channel's template into its own prefixed field group.
    const contents = campaign.contents || [];
    const byChannel = Object.fromEntries(contents.map((c) => [c.channel, c.template || {}]));
    // attribution_link_id is a sibling of template on each content row, not
    // inside it — pulled separately so ShortLinkField can show what's
    // already tagged when reopening this campaign for edit.
    const linkByChannel = Object.fromEntries(contents.map((c) => [c.channel, c.attribution_link_id || '']));
    const settings = campaign.delivery_settings || {};
    const scheduled = campaign.scheduled_at ? new Date(campaign.scheduled_at) : null;

    const push = byChannel.push || {};
    const wa = byChannel.whatsapp || {};
    const sms = byChannel.sms || {};
    const email = byChannel.email || {};
    const rcs = byChannel.rcs || {};

    const prefill = {
      editingId: campaign.id,
      editingSegmentId: campaign.segment_id,
      editingLaunchedAt: campaign.launched_at,
      campName: campaign.name,
      channels: contents.length ? contents.map((c) => c.channel) : ['push'],
      quietHours: settings.quietHours ?? DEFAULT_STATE.quietHours,
      stopOnConvert: settings.stopOnConvert ?? DEFAULT_STATE.stopOnConvert,
      freqCap: settings.freqCap ?? DEFAULT_STATE.freqCap,
      controlGroup: settings.controlGroup ?? DEFAULT_STATE.controlGroup,
      schedType: scheduled ? 'scheduled' : 'now',
      schedDate: scheduled ? scheduled.toISOString().slice(0, 10) : '',
      schedTime: scheduled ? scheduled.toISOString().slice(11, 16) : '',
      msgTitle: push.title || '',
      msgBody: push.body || '',
      msgRedirectionType: push.redirectionType || '',
      msgRedirectionValue: push.redirectionValue || '',
      msgImageUrl: push.imageUrl || '',
      waTemplateName: wa.templateName || '',
      waTemplateParams: (wa.templateParams || []).join(', '),
      waImageUrl: wa.imageUrl || '',
      waAttributionLinkId: linkByChannel.whatsapp || '',
      smsText: sms.text || '',
      smsTemplateId: sms.templateId || '',
      smsAttributionLinkId: linkByChannel.sms || '',
      emailSubject: email.subject || '',
      emailHtml: email.html || '',
      rcsTemplateId: rcs.templateId || '',
      rcsTemplateParams: (rcs.templateParams || []).join(', '),
      rcsAttributionLinkId: linkByChannel.rcs || '',
    };

    segmentService.getById(campaign.segment_id)
      .then((segment) => this.setState({ ...prefill, initialSegment: segment, open: true }))
      .catch(() => this.setState({ ...prefill, open: true }));
  }

  _closeHandler() { this.setState({ open: false }); }

  setField(key, val) { this.setState({ [key]: val }); }

  next() {
    this.setState(s => ({ step: Math.min(STEPS.length - 1, s.step + 1) }));
  }

  back() {
    this.setState(s => ({ step: Math.max(0, s.step - 1) }));
  }

  // Resolves the audience picked in Step1 into a real segment_id — creating an
  // ad-hoc segment from the filter builder's conditions if the user didn't
  // pick an already-saved one. When editing and the audience step was never
  // touched, reuse the campaign's existing segment rather than creating a
  // pointless duplicate.
  async _resolveSegmentId() {
    const { audience, editingSegmentId } = this.state;
    if (!audience && editingSegmentId) return editingSegmentId;
    if (audience?.mode === 'saved' && audience.selectedSegId) {
      return audience.selectedSegId;
    }
    if (audience?.filters?.length) {
      const conditions = filtersToConditions(audience.filters);
      if (!conditions.length) return editingSegmentId || null;
      const segment = await segmentService.create({
        name: this.state.campName ? `Audience — ${this.state.campName}` : `Campaign audience — ${new Date().toISOString()}`,
        conditions,
        match_type: toMatchType(audience.op),
      });
      return segment.id;
    }
    return editingSegmentId || null;
  }

  _buildScheduledAt() {
    const { schedType, schedDate, schedTime } = this.state;
    if (schedType !== 'scheduled' || !schedDate) return undefined;
    return new Date(`${schedDate}T${schedTime || '00:00'}:00`).toISOString();
  }

  _buildChannelTemplate(channel) {
    const {
      msgTitle, msgBody, msgRedirectionType, msgRedirectionValue, msgImageUrl,
      waTemplateName, waTemplateParams, waImageUrl,
      smsText, smsTemplateId, emailSubject, emailHtml,
      rcsTemplateId, rcsTemplateParams,
    } = this.state;

    if (channel === 'whatsapp') {
      return {
        templateName: waTemplateName,
        templateParams: waTemplateParams.split(',').map((s) => s.trim()).filter(Boolean),
        ...(waImageUrl ? { imageUrl: waImageUrl } : {}),
      };
    }
    if (channel === 'sms') {
      return { text: smsText, ...(smsTemplateId ? { templateId: smsTemplateId } : {}) };
    }
    if (channel === 'email') {
      return { subject: emailSubject, html: emailHtml };
    }
    if (channel === 'rcs') {
      return { templateId: rcsTemplateId, templateParams: rcsTemplateParams.split(',').map((s) => s.trim()).filter(Boolean) };
    }
    return {
      title: msgTitle, body: msgBody,
      ...(msgRedirectionType ? { redirectionType: msgRedirectionType } : {}),
      ...(msgRedirectionValue ? { redirectionValue: msgRedirectionValue } : {}),
      ...(msgImageUrl ? { imageUrl: msgImageUrl } : {}),
    };
  }

  // Sibling of the channel's template (not part of it) — the short link
  // ShortLinkField tagged into that channel's message, if any. Only
  // sms/whatsapp/rcs render ShortLinkField at all (push/email already have
  // real click tracking), so push/email always resolve to null here.
  _attributionLinkIdFor(channel) {
    const { waAttributionLinkId, smsAttributionLinkId, rcsAttributionLinkId } = this.state;
    if (channel === 'whatsapp') return waAttributionLinkId || null;
    if (channel === 'sms') return smsAttributionLinkId || null;
    if (channel === 'rcs') return rcsAttributionLinkId || null;
    return null;
  }

  _buildChannels() {
    return this.state.channels.map((channel) => ({
      channel,
      template: this._buildChannelTemplate(channel),
      attribution_link_id: this._attributionLinkIdFor(channel),
    }));
  }

  async _buildCampaignPayload() {
    const { campName, quietHours, stopOnConvert, freqCap, controlGroup } = this.state;
    const segment_id = await this._resolveSegmentId();
    if (!segment_id) {
      throw new Error('Please select or build an audience first.');
    }
    return {
      name: campName,
      segment_id,
      channels: this._buildChannels(),
      delivery_settings: { quietHours, stopOnConvert, freqCap, controlGroup },
      scheduled_at: this._buildScheduledAt(),
    };
  }

  _save() {
    const { editingId } = this.state;
    return this._buildCampaignPayload().then((payload) => (
      editingId ? campaignService.update(editingId, payload) : campaignService.create(payload)
    ));
  }

  draft() {
    const { campName, editingId } = this.state;
    if (!campName.trim()) {
      EventBus.emit('toast', { type: 'error', message: 'Please enter a campaign name.' });
      return;
    }
    this.setState({ saving: true });
    this._save()
      .then(() => {
        this.setState({ saving: false, open: false });
        EventBus.emit('toast', { type: 'info', message: editingId ? 'Campaign updated.' : 'Draft saved.' });
      })
      .catch((err) => {
        this.setState({ saving: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to save draft.' });
      });
  }

  launch() {
    const { campName, schedType, editingId, editingLaunchedAt } = this.state;
    if (!campName.trim()) {
      EventBus.emit('toast', { type: 'error', message: 'Please enter a campaign name.' });
      return;
    }
    this.setState({ saving: true });
    this._save()
      .then((campaign) => {
        // Already launched before (paused mid-send) — don't re-trigger a
        // launch from here, that's what the dedicated Resume action is for.
        if (editingLaunchedAt) return campaign;
        // 'now' and 'optimal' both send immediately — there's no send-time
        // optimization model yet, so 'optimal' is treated the same as 'now'.
        if (schedType !== 'scheduled') return campaignService.launch(campaign.id);
        return campaign;
      })
      .then(() => {
        this.setState({ saving: false, open: false });
        EventBus.emit('toast', {
          type: 'success',
          message: editingId ? `Campaign "${campName}" updated.` : `Campaign "${campName}" launched!`,
        });
      })
      .catch((err) => {
        this.setState({ saving: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to launch campaign.' });
      });
  }

  openTestSend(channel) { this.setState({ testSendChannel: channel }); }

  closeTestSend() { this.setState({ testSendChannel: null }); }

  toggleChannel(key) {
    this.setState((s) => {
      const has = s.channels.includes(key);
      // Keep at least one channel selected — deselecting the last is a no-op.
      if (has && s.channels.length === 1) return null;
      return { channels: has ? s.channels.filter((c) => c !== key) : [...s.channels, key] };
    });
  }

  render() {
    const {
      open, step, saving, editingId, editingLaunchedAt, initialSegment, audience, channels, channelStats,
      msgTitle, msgBody, msgRedirectionType, msgRedirectionValue, msgImageUrl,
      waTemplateName, waTemplateParams, waImageUrl, waAttributionLinkId,
      smsText, smsTemplateId, smsAttributionLinkId, emailSubject, emailHtml,
      rcsTemplateId, rcsTemplateParams, rcsAttributionLinkId,
      schedType, schedDate, schedTime, schedTZ, quietHours, stopOnConvert, freqCap, controlGroup, campName,
      testSendChannel,
    } = this.state;

    const isLast = step === STEPS.length - 1;
    const pct    = ((step + 1) / STEPS.length) * 100;

    return (
      <>
        <Modal
          open={open}
          onClose={() => this.setState({ open: false })}
          width="720px"
          closable={false}
          bodyPadding={false}
        >
          {/* Wizard header — title/close on their own row so the labeled
              5-step Stepper (which needs ~550px on its own) always has room,
              rather than fighting the title and close button for space on
              one row (that combination doesn't fit in a 720px modal). */}
          <div className={styles.wHead}>
            <div className={styles.wHeadTop}>
              <div className={styles.wHeadLeft}>
                <div className={styles.wStep}>Step {step + 1} of {STEPS.length}</div>
                <div className={styles.wTitle}>
                  {editingId ? 'Edit campaign — ' : ''}
                  {['Select your audience','Choose channel','Compose message','Set schedule','Name & launch'][step]}
                </div>
              </div>
              <button className={styles.wClose} onClick={() => this.setState({ open: false })}><Icon name="close" size={18} /></button>
            </div>
            <Stepper steps={STEPS} current={step} />
          </div>

          {/* Progress bar */}
          <div className={styles.wProgress}>
            <div className={styles.wProgressFill} style={{ width: pct + '%' }} />
          </div>

          {/* Step body */}
          <div className={styles.wBody}>
            {step === 0 && (
              <Step1Audience initialSegment={initialSegment} onChange={aud => this.setState({ audience: aud })} />
            )}
            {step === 1 && <Step2Channel value={channels} onToggle={this.toggleChannel} channelStats={channelStats} />}
            {step === 2 && (
              <Step3Message
                channels={channels}
                msgTitle={msgTitle} msgBody={msgBody} msgRedirectionType={msgRedirectionType} msgRedirectionValue={msgRedirectionValue} msgImageUrl={msgImageUrl}
                waTemplateName={waTemplateName} waTemplateParams={waTemplateParams} waImageUrl={waImageUrl} waAttributionLinkId={waAttributionLinkId}
                smsText={smsText} smsTemplateId={smsTemplateId} smsAttributionLinkId={smsAttributionLinkId}
                emailSubject={emailSubject} emailHtml={emailHtml}
                rcsTemplateId={rcsTemplateId} rcsTemplateParams={rcsTemplateParams} rcsAttributionLinkId={rcsAttributionLinkId}
                onField={this.setField}
                onTestSend={this.openTestSend}
              />
            )}
            {step === 3 && (
              <Step4Schedule
                schedType={schedType} schedDate={schedDate} schedTime={schedTime} schedTZ={schedTZ}
                quietHours={quietHours} stopOnConvert={stopOnConvert} freqCap={freqCap} controlGroup={controlGroup}
                onField={this.setField}
              />
            )}
            {step === 4 && (
              <Step5Review
                campName={campName} audience={audience} channels={channels} channelStats={channelStats}
                msgTitle={msgTitle} waTemplateName={waTemplateName} smsText={smsText} emailSubject={emailSubject}
                rcsTemplateId={rcsTemplateId}
                schedType={schedType}
                onField={this.setField}
              />
            )}
          </div>

          {/* Footer */}
          <div className={styles.wFoot}>
            <div>
              <button className={styles.ghostBtn} onClick={this.draft}>Save draft</button>
            </div>
            <div className={styles.wFootRight}>
              {step > 0 && (
                <button className={styles.ghostBtn} onClick={this.back}>← Back</button>
              )}
              {!isLast && (
                <button className={styles.nextBtn} onClick={this.next}>Continue →</button>
              )}
              {isLast && (
                <button className={styles.launchBtn} disabled={saving} onClick={this.launch}>
                  {saving
                    ? 'Saving…'
                    // Keyed off editingLaunchedAt (has this campaign EVER
                    // launched), not editingId (is this an edit of any kind) —
                    // a never-launched draft still needs to show "Launch
                    // Campaign" on edit, since launch() still fires for it.
                    // Only a previously-launched-then-paused campaign should
                    // read "Save Changes" (re-launching those is Resume's job).
                    : editingLaunchedAt
                      ? <><Icon name="save" size={15} /> Save Changes</>
                      : <><Icon name="rocket_launch" size={15} /> Launch Campaign</>}
                </button>
              )}
            </div>
          </div>
        </Modal>

        <TestSendModal
          open={!!testSendChannel}
          channel={testSendChannel}
          template={testSendChannel ? this._buildChannelTemplate(testSendChannel) : null}
          onClose={this.closeTestSend}
        />
      </>
    );
  }
}

export default CampaignWizard;
